import { CheckBoxSwitcher, FormInput, Modal, PrimaryButton } from '@systemeio/ui';
import { isClient } from '@systemeio/util';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { InfoBlockOrderedListItem } from 'shared/components/info-block-ordered-list-item';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { handle422Error } from 'shared/utils/parse-422-errors';
import { twJoin } from 'tailwind-merge';
import { defaultEventTypesValues } from '../constants/event-types';
import { useWebhookSettingsApi } from '../hooks/use-webhook-settings-api';
import { WebhookCreateInterface, WebhookErrorsInterface, WebhookInterface, WebhookParamsInterface, WebhookSubscriptionEventEnum } from '../types/webhook-settings-interface';
import { getCheckedEventTypes } from '../utils/get-checked-event-types';
import { getDiffBody } from '../utils/get-diff-body';
import { mapSubscriptions } from '../utils/map-subscriptions';
import EventTypesSelect from './event-types-select';
import WebhookFormTitle from './form-title';
const defaultValues: WebhookParamsInterface = {
  name: '',
  secret: '',
  url: '',
  active: true
};
const defaultErrors: WebhookErrorsInterface = {
  name: '',
  secret: '',
  url: '',
  subscriptions: '',
  active: ''
};
interface WebhookModalProps {
  onSave?: (webhook: WebhookInterface) => void;
  webhook?: WebhookInterface;
  isOpen: boolean;
  onClose: () => void;
}
const WebhookModal = ({
  onSave,
  isOpen,
  onClose,
  webhook
}: WebhookModalProps) => {
  const {
    t
  } = useLocoTranslation();
  const isNew = !webhook;
  const [isLoading, setIsLoading] = useState(false);
  const [webhookParams, setWebhookParams] = useState<WebhookParamsInterface>(defaultValues);
  const [errors, setErrors] = useState(defaultErrors);
  const [selectedEvents, setSelectedEvents] = useState(defaultEventTypesValues);
  const {
    create,
    edit
  } = useWebhookSettingsApi();
  const handleSelectEvent = (eventType: WebhookSubscriptionEventEnum) => setSelectedEvents(se => ({
    ...se,
    [eventType]: !se[eventType]
  }));
  const handleParamChange = (param: keyof WebhookCreateInterface) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setWebhookParams(wp => ({
      ...wp,
      [param]: value
    }));
  };
  const handleClose = () => {
    setErrors(defaultErrors);
    setWebhookParams(defaultValues);
    setSelectedEvents(defaultEventTypesValues);
    onClose();
  };
  const handleCreate = () => create({
    ...webhookParams,
    subscriptions: mapSubscriptions(selectedEvents)
  });
  const handleEdit = (previousWebhookData: WebhookInterface) => edit(previousWebhookData.id, getDiffBody(webhookParams, selectedEvents, previousWebhookData));
  const handleSubmit = async (event_0: FormEvent<HTMLFormElement>) => {
    event_0.preventDefault();
    setIsLoading(true);
    setErrors(defaultErrors);
    const promise = isNew ? handleCreate() : handleEdit(webhook);
    try {
      setErrors(defaultErrors);
      const data = await promise;
      onSave?.(data);
      handleClose();
    } catch (error) {
      handle422Error(error, setErrors);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (webhook?.id) {
      setWebhookParams({
        name: webhook.name,
        url: webhook.url,
        active: webhook.active
      });
      setSelectedEvents(getCheckedEventTypes(webhook.subscriptions));
    }
  }, [webhook?.id]);
  const actionCaption = twJoin(isNew ? t('global.create') : t('global.edit'), t('dashboard.user_settings.webhooks.title'));
  return <Modal opened={isOpen} onClose={handleClose} title={actionCaption} contentClassName="w-full pt-0 px-0 lg:px-0 lg:pt-5" isFetching={isLoading}>
      <form onSubmit={handleSubmit}>
        <WebhookFormTitle>{t('dashboard.user_settings.webhooks.info_title')}</WebhookFormTitle>
        <ul className="flex flex-col gap-6 p-6">
          <InfoBlockOrderedListItem>
            {t('dashboard.user_settings.webhooks.instruction_first')}
          </InfoBlockOrderedListItem>
          <InfoBlockOrderedListItem>
            {t('dashboard.user_settings.webhooks.instruction_second')}
          </InfoBlockOrderedListItem>
        </ul>
        <hr className="text-fill-primary" />
        <div className="flex w-1/2 flex-col gap-5 py-6 pl-6">
          <FormInput label={t('global.name')} value={webhookParams.name} placeholder={t('dashboard.user_settings.webhooks.placeholder.name')} onChange={handleParamChange('name')} error={errors.name} disabled={isLoading} />
          <FormInput label={t('global.url')} placeholder={isClient() ? window.location.origin : ''} required value={webhookParams.url} onChange={handleParamChange('url')} error={errors.url} disabled={isLoading || !isNew} />
          <FormInput label={t('dashboard.user_settings.webhooks.secret')} value={webhookParams.secret} required placeholder={t('dashboard.user_settings.webhooks.placeholder.secret')} onChange={handleParamChange('secret')} error={errors.secret} disabled={isLoading} />
          <CheckBoxSwitcher label={t('global.active')} onChange={val => setWebhookParams(params => ({
          ...params,
          active: val
        }))} selected={webhookParams.active} wrapperClassName="flex items-center" labelClassName="text-base" />
        </div>
        <WebhookFormTitle>{t('dashboard.user_settings.webhooks.events_title')}</WebhookFormTitle>
        <EventTypesSelect disabled={isLoading} selected={selectedEvents} onSelect={handleSelectEvent} error={errors.subscriptions} />
        <hr className="text-fill-primary" />
        <div className="flex justify-center pt-6">
          <PrimaryButton isFetching={isLoading} type="submit">
            {t('global.save')}
          </PrimaryButton>
        </div>
      </form>
    </Modal>;
};
export default WebhookModal;